// FloorplanViewer.jsx (핵심만 재구성)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import api from '../utils/api';
import useSmartPoll from './useSmartPoll';
import useJwtRole from './useJwtRole';
import LocationJumpToolbar from './LocationJumpToolbar';
import DeviceControl from '../Pages/DeviceControl';
import ActiveUserPanel from './ActiveUserPanel';
import '../css/FloorplanViewer.css';
import useAlarmSound from './useAlarmSound';

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');
const CLOSE_ALERT_DEDUP_MS = 60_000;

export default function FloorplanViewer({ propLocationName, propFloorName, disableUrlSync = false }) {
  const { locationName: urlLocName, floorName: urlFloorName } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const role = useJwtRole();
  const isAdmin = String(role).toUpperCase() === 'ADMIN';

  // URL/prop 우선순위
  const effectiveLocationName = propLocationName ?? urlLocName;
  const effectiveFloorNameFromOutside = propFloorName ?? urlFloorName;

  // 상태
  const [location, setLocation] = useState(state?.location || null);
  const [devices, setDevices] = useState([]);
  const [localFloorName, setLocalFloorName] = useState(effectiveFloorNameFromOutside || '');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [showAlertPanel, setShowAlertPanel] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [allDevices, setAllDevices] = useState([]);
  // 알림/디듀프 메모리
  const prevAlarmSigRef = useRef(new Map());
  const prevConnRef = useRef(new Map());
  const notifyMemoRef = useRef(new Map()); // conn:/rec:
  const hazardMemoRef = useRef(new Map());
  const initializedRef = useRef(false);
  const { startLoop, stop, enabled, setEnabled } = useAlarmSound({ mutedByDefault: true });

  const [scanPhase, setScanPhase] = useState(0);
  

  useEffect(() => setLocalFloorName(effectiveFloorNameFromOutside || ''), [effectiveFloorNameFromOutside]);

  // 위치 메타 1회 로드
  useEffect(() => {
    if (location || !effectiveLocationName) return;
    const ac = new AbortController();
    (async () => {
      try {
        const res = await api.get('/location', { params: { name: effectiveLocationName }, signal: ac.signal });
        setLocation(res.data || null);
      } catch (e) {
        if (e?.name !== 'CanceledError') console.error('로케이션 로딩 실패', e);
      }
    })();
    return () => ac.abort();
  }, [effectiveLocationName, location]);

const openEditor = () => {
  if (!location?.name || !selectedFloor?.name) return;
  navigate(
    `/floorplan/${encodeURIComponent(location.name)}/${encodeURIComponent(selectedFloor.name)}/edit`,
    // 선택: 메타를 다시 불러오지 않게 state로 넘겨 재사용
    { state: { location } })
};


useEffect(() => {
  let frame;
  let last = performance.now();
  const speed = 2; // 숫자 키우면 더 빠르게 좌우 스윙

  const loop = (now) => {
    const dt = (now - last) / 1000; // 초 단위
    last = now;

    // phase를 계속 증가시키면 sin(phase)가 -1~+1로 왕복
    setScanPhase((p) => p + dt * speed);

    frame = requestAnimationFrame(loop);
  };

  frame = requestAnimationFrame(loop);
  return () => cancelAnimationFrame(frame);
}, []);


const getDeviceRotation = (d = {}) => {
  const dirRaw = d.installedDirection ?? d.InstalledDirection;
  if (!dirRaw) return 0;

  const dir = String(dirRaw).toLowerCase().trim();

  // 복합값(simple + multi-direction) 모두 처리
  if (dir.includes('up')) return 0;       // 위
  if (dir.includes('right')) return 90;   // 오른쪽
  if (dir.includes('down')) return 180;   // 아래
  if (dir.includes('left')) return 270;   // 왼쪽

  // 문자열이 이상하면 기본값
  return 0;
};



  // 선택된 층
  const selectedFloor = useMemo(() => {
    if (!location?.floors?.length) return null;
    if (localFloorName) return location.floors.find(f => f.name === localFloorName) || location.floors[0];
    return location.floors[0];
  }, [location?.floors, localFloorName]);

  // 초기 진입 시 URL 보정
  useEffect(() => {
    if (disableUrlSync || !location?.name || !location.floors?.length || urlFloorName) return;
    navigate(`/floorplan/${encodeURIComponent(urlLocName)}/${encodeURIComponent(location.floors[0].name)}`, { replace: true, state: { location } });
  }, [disableUrlSync, location, urlFloorName, urlLocName, navigate]);

const getDeviceIcon = (d = {}) => {
  const connected = Boolean(d?.isConnected);
  const smoke     = Number(d?.smoke) === 1;
  const blackout  = Number(d?.blackout) === 1;
  const ff        = Number(d?.ffFacility) === 1;
  const motor     = Number(d?.motorFlag) === 1;
  const verkada   = Boolean(d?.verkada);

  // 🔍 진단 로그 (한 번만 찍고 싶으면 조건 걸어도 됨)
  console.log('[ICON CHECK]', d.id, {
    verkada_raw: d?.verkada,
    verkada_bool: Boolean(d?.verkada),
    motorFlag: motor,
    smoke, blackout, ff
  });

  if (!connected) return '/images/LES_disconnected_ICON.png';
  if (smoke)      return '/images/LES_smoke_ICON.png';
  if (blackout)   return '/images/LES_blackout_ICON.png';
  if (ff)         return '/images/LES_facility_ICON.png';
  if (verkada)    return '/images/verkada.png';
  if (!smoke && !blackout && !ff && !verkada && motor) return '/images/LES_test_ICON.png';
  return '/images/LES_normal_ICON.png';
};




// 끊겨 있으면 알람 아님(유지)
const deviceHasAlarm = (d) => Boolean(d?.isConnected) && Number(d?.motorFlag) === 1;

  // 알림 도우미(간단 버전)
const notifyOnce = useCallback((key, title, body) => {
  const now = Date.now();
  const last = notifyMemoRef.current.get(key) || 0;
  if (now - last < CLOSE_ALERT_DEDUP_MS) return;

  // 권한 없으면 요청(처음 한 번만)
  if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
    try { Notification.requestPermission().catch(() => {}); } catch {}
  }

  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try {
      // ✨ 핵심: requireInteraction: true
      const n = new Notification(title, {
        body,
        tag: key,                  // 같은 key면 대체
        icon: '/images/LES_alert_ICON.png',
        requireInteraction: true,  // ← 유저 조작 전까지 자동으로 안 사라짐
        renotify: true,            // 같은 tag 다시 올릴 때 알림 강조
        // badge: '/images/pwa-badge.png', // PWA면 선택
        // silent: false,                   // 소리 설정 원하면
      });

      // 클릭 시 포커스/닫기(원하면 패널도 열기)
      n.onclick = () => {
        try { window.focus(); } catch {}
        // 예: 클릭하면 알람 패널 열고 싶을 때
        try { setShowAlertPanel(true); } catch {}
        n.close();
      };

      // 사용자가 X 눌러 닫아도 OK (자동 close 없음)
    } catch {}
  }
  notifyMemoRef.current.set(key, now);
}, []);




// ⛳ FloorplanViewer.jsx 내: 선택된 층 장비 폴링 (useSmartPoll 제거 버전)
useEffect(() => {
  if (!location?.name || !selectedFloor?.name) return;

  let alive = true;                     // 언마운트/층 변경 시 결과 무시용
  let timer = null;
  let ac = new AbortController();

  const fetchOnce = async () => {
    // 층 바뀌거나 unmount되면 early return
    if (!alive) return;

    // 이전 요청 취소 후 새 컨트롤러
    ac.abort();
    ac = new AbortController();

    try {
      const { data } = await api.get('/devices', {
        params: { location: location.name, floor: selectedFloor.name },
        signal: ac.signal,
      });
      if (alive) setDevices(Array.isArray(data) ? data : []);
    } catch (e) {
      // 취소(CanceledError/ERR_CANCELED)는 무시
      if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
        console.error(e);
      }
    } finally {
      if (alive) {
        // 고정 주기 재예약 (원하시는 주기)
        timer = setTimeout(fetchOnce, 1500);
      }
    }
  };

  fetchOnce();

  return () => {
    alive = false;          // 이후 결과는 버림
    ac.abort();             // 진행 중 요청 취소
    if (timer) clearTimeout(timer); // 다음 tick 취소
  };
}, [location?.name, selectedFloor?.name]); // ← 오직 선택된 층만 deps


  // 전체 층 모니터링 폴링(겹치지 않게)
useEffect(() => {
  if (!location?.name) return;
  let alive = true, timer = null, ac = new AbortController();

  const tick = async () => {
    if (!alive) return;
    ac.abort(); ac = new AbortController();

    try {
      const { data } = await api.get('/devices/by-location', {
        params: { name: location.name },
        signal: ac.signal,
      });
      if (!alive) return;

      const all = Array.isArray(data) ? data : [];
      const dedupAll = Array.from(new Map(all.map(d => [d.id, d])).values());
      setAllDevices(dedupAll);
      // 알람 리스트
      const list = [];
      const prevMap = prevAlarmSigRef.current;
      const seen = new Set();

      const sig = (d) => `${d?.isConnected ? 1 : 0}:${+d?.smoke || 0}:${+d?.blackout || 0}:${+d?.ffFacility || 0}:${+d?.motorFlag || 0}`;

      for (const d of all) {
        if (!d?.id) continue;
        if (deviceHasAlarm(d)) list.push({ floorName: d.floor, device: d });

        const curr = sig(d);
        const prev = prevMap.get(d.id);
        if (prev !== curr) {
          const wasAlarm = prev ? prev.split(':').map(Number) : null;
          const prevHas = wasAlarm ? (wasAlarm[4] === 1 && wasAlarm[0] === 1) : false;
          const nowHas = deviceHasAlarm(d);
          if (nowHas && !prevHas) notifyOnce(`alarm:${d.id}`, `경보: ${d.id}`, `위치: ${d.floor || '-'}`);
          if (!nowHas && prevHas) notifyOnce(`clear:${d.id}`, `경보 해제: ${d.id}`, `위치: ${d.floor || '-'}`);
        }
        prevMap.set(d.id, curr);
        seen.add(d.id);
      }
      // 사라진 디바이스 정리
      for (const k of prevMap.keys()) if (!seen.has(k)) prevMap.delete(k);

      // 연결 변화 알림
      if (!initializedRef.current) {
        const m = prevConnRef.current; m.clear();
        for (const d of all) m.set(d.id, Boolean(d.isConnected));
        initializedRef.current = true;
      } else {
        const m = prevConnRef.current;
        for (const d of all) {
          const nowConn = Boolean(d.isConnected);
          const prev = m.get(d.id);
          if (prev !== undefined && prev !== nowConn) {
            if (prev === true && nowConn === false) notifyOnce(`connDown:${d.id}`, `연결 끊김: ${d.id}`, `위치: ${d.floor || '-'}`);
            if (prev === false && nowConn === true) notifyOnce(`connUp:${d.id}`, `연결 복구: ${d.id}`, `위치: ${d.floor || '-'}`);
          }
          m.set(d.id, nowConn);
        }
      }

      setAlerts(list);



  } catch (e) {
      if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') console.error(e);
    } finally {
      if (alive) timer = setTimeout(tick, 2000);
    }
  };

  tick();
  return () => { alive = false; ac.abort(); if (timer) clearTimeout(timer); };
}, [location?.name]);
 // 알람이 하나라도 있으면 반복 재생, 없으면 정지
  useEffect(() => {
    const anyAlarm = alerts.length > 0;
    if (anyAlarm) startLoop('alarm');
    else stop('alarm');
  }, [alerts, startLoop, stop]);

  // 사용자가 사운드를 끄면 즉시 정지
  useEffect(() => {
    if (!enabled) stop('alarm');
  }, [enabled, stop]);
  // ====== 여기(B) 끝 ======

function parseScanAngle(scanAngle) {
  if (!scanAngle) return 0;
  const matched = String(scanAngle).match(/\d+/);  // 숫자만 추출
  return matched ? Number(matched[0]) : 0;
}


function getBaseAngle(installed) {
  switch ((installed || '').toLowerCase()) {
    case 'up':    return 0;
    case 'right': return 90;
    case 'down':  return 180;
    case 'left':  return 270;
    default:      return 0;
  }
}


function computeLaserAngle(installed, motor, scanAngle) {
  // 1) 설치 방향 기준각
  const base = {
    up: 0,
    right: 90,
    down: 180,
    left: 270,
  }[installed] ?? 0;

  // 2) scanAngle을 "30도" → 30 으로 파싱
  const angle = parseScanAngle(scanAngle);
  const hasScan = angle > 0;

  // 3) motor 방향
  const dir = (motor || "").toLowerCase();

  let offset = 0;

  if (dir === "straight") {
    offset = 0;
  } else if (dir === "left") {
    offset = hasScan ? -angle : -90;  // scanAngle 없으면 기본 -90
  } else if (dir === "right") {
    offset = hasScan ? angle : 90;    // scanAngle 없으면 기본 +90
  }

  // 4) 최종 계산
  let final = base + offset;
  if (final < 0) final += 360;
  return final % 360;
}
function computeLaserScanAngle(installed, motor, scanAngle, phase) {
  const base = getBaseAngle(installed);
  const angle = parseScanAngle(scanAngle);
  const hasScan = angle > 0;
  const delta = hasScan ? angle : 90;   // 기본 90도 좌우

  const dir = (motor || "").toLowerCase();

  // 🔥 Both: baseAngle 기준으로 좌우 대칭 스윙
  if (dir === "both") {
    // mid = base
    // amp = delta
    return base + delta * Math.sin(phase);   // -delta ~ +delta 범위로 흔들림
  }

  // ⬇ 기존 단일 방향 스윙 (Straight / Left / Right)
  const target = computeLaserAngle(installed, motor, scanAngle);

  let diff = target - base;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;

  const mid = base + diff / 2;
  const amp = Math.abs(diff) / 2;

  return mid + amp * Math.sin(phase);
}


  const chosenImageUrl = useMemo(() => {
    const url = selectedFloor?.floorplanUrl;
    if (!url) return '';
    return url.startsWith('http') ? url : `${API_BASE}${url}`;
  }, [selectedFloor]);

const sendControlCommandAll = async (manualValue) => {
  if (allDevices.length === 0) {
    alert('디바이스가 없습니다.');
    return;
  }

  const actionText = manualValue === 'on' ? '전체 경보(ON)' : '전체 종료(OFF)';
  const sampleIds = allDevices.slice(0, 5).map(d => d.id).join(', ');
  const more = allDevices.length > 5 ? ` 외 ${allDevices.length - 5}대` : '';

  const okToSend = window.confirm(
    `${actionText} 명령을 ${allDevices.length}대에 전송할까요?\n\n` +
    `예시: ${sampleIds}${more}`
  );
  if (!okToSend) return;

  setBulkSending(true);
  try {
    const results = await Promise.allSettled(
      allDevices.map(d =>
        api.post('/control', { id: d.id, modechange: 'off', manual: manualValue })
      )
    );
    const ok = results.filter(r => r.status === 'fulfilled' && r.value?.status >= 200 && r.value?.status < 300).length;
    const fail = results.length - ok;

    // 완료 후에도 결과 안내를 alert로!
    alert(`✅ 일괄 전송 완료\n\n성공: ${ok}건\n실패: ${fail}건`);
  } catch (e) {
    console.error(e);
    alert('❌ 일괄 전송 중 오류가 발생했습니다.');
  } finally {
    setBulkSending(false);
  }
};

  if (!location) return <div className="panel-placeholder">위치 정보를 불러오는 중…</div>;
  if (!selectedFloor) return <div className="panel-placeholder">{location.name}에 등록된 층 정보가 없습니다.</div>;
  if (!chosenImageUrl) return <div className="panel-placeholder">도면 이미지가 없습니다.</div>;

  return (
    
    <div className="floorplan-viewer-root">
              {isAdmin && (
  <ActiveUserPanel refreshMs={60_000} />
        )}

          <div className="device-topbar">

        <button
    className="device-topbar__btn"
    onClick={() => setEnabled(!enabled)}
     title={enabled ? '알람 사운드 끄기' : '알람 사운드 켜기'}
    >     {enabled ? '🔊 사운드 ON' : '🔇 사운드 OFF'}
    </button>


          <span className="device-topbar__count">
            {location.name} — {selectedFloor.name} · 층 {devices.length}대 / 전체 {allDevices.length}대
          </span>
          {alerts.length > 0 && <span className="alarm-flag">ALERT</span>}
          <div className="device-topbar__buttons">
            <button className="device-topbar__btn device-topbar__btn--on" onClick={() => sendControlCommandAll('on')} disabled={bulkSending || !allDevices.length}>
              전체 경보
            </button>
            <button className="device-topbar__btn device-topbar__btn--off" onClick={() => sendControlCommandAll('off')} disabled={bulkSending || !allDevices.length}>
              전체 종료
            </button>
             {isAdmin && (
    <button
      className="device-topbar__btn"
      onClick={openEditor}
      title="이 층의 디바이스 위치 편집"
    >
      위치 편집
    </button>
  )}
          </div>
        </div>
      {alerts.length > 0 && <div className="alarm-overlay" aria-hidden="true" />}
      {alerts.length > 0 && (
        <button className="global-alert-button" onClick={() => setShowAlertPanel(true)} title="알람 목록 보기">
          ALERT {alerts.length}
        </button>
      )}

      <div className="floorplan-viewer-map">
        <div style={{ marginBottom: 8 }}>
          <LocationJumpToolbar
            locationName={location.name}
            currentFloorName={selectedFloor.name}
            onSelectFloor={(floor) => { setLocalFloorName(floor); setSelectedDevice(null); }}
            useNavigation={false}
          />
        </div>

      


        <TransformWrapper key={`${location.name}-${selectedFloor.name}`} initialScale={1} limitToBounds boundsPadding={0}>
          <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
            <div className="zoom-image-wrapper-map">
              <img src={chosenImageUrl} alt={`${location.name} ${selectedFloor.name}`} className="floorplan-image" />
              {devices.map((d) =>
                d.x != null && d.y != null ? (
                  <div
                    key={d.id}
                    className="device-marker"
                    style={{ left: `${d.x * 100}%`, top: `${d.y * 100}%` }}
                    onClick={() => setSelectedDevice(d)}
                    title={d.id}
                  >
                    <BatteryBadge adc={d.bat_adc_value} />
                <img
                     src={getDeviceIcon(d)}
                     alt=""
                     className="device-marker-img"
                    
                    />
    <div
        className="laser-beam"
        style={{
          transform: `rotate(${computeLaserScanAngle(
            d.installedDirection,
            d.motorDir,
            d.scanAngle,
            scanPhase, // 전역 phase
          )}deg)`,
          '--laser-len': '70px', // 원하면 scanAngle 기반으로 길이 조절도 가능
        }}
      />

                    <div className="device-label">{d.id}</div>
                  </div>
                ) : null
              )}
            </div>
          </TransformComponent>
        </TransformWrapper>
      </div>

      <aside className={`device-control-panel ${selectedDevice ? 'open' : ''}`}>
        <div className="panel-header"><button className="panel-close" onClick={() => setSelectedDevice(null)}>✕</button></div>
        {selectedDevice ? <DeviceControl defaultDeviceId={selectedDevice.id} currentDeviceData={selectedDevice} /> : <div className="panel-placeholder">마커를 클릭하면 제어 패널이 표시됩니다.</div>}
      </aside>

      {showAlertPanel && (
        <div className="alert-modal-backdrop" onClick={() => setShowAlertPanel(false)}>
          <div className="alert-modal" onClick={(e) => e.stopPropagation()}>
            <div className="alert-modal__header">
              <strong>알람 목록</strong>
              <button className="panel-close" onClick={() => setShowAlertPanel(false)}>✕</button>
            </div>
            <div className="alert-modal__body">
              {alerts.length === 0 ? (
                <div className="ljt-empty">현재 알람이 없습니다.</div>
              ) : (
                alerts.map(({ floorName, device }) => (
                  <div key={`${floorName}-${device.id}`} className="alert-item" onClick={() => { setLocalFloorName(floorName); setSelectedDevice(device); setShowAlertPanel(false); }}>
                    <div className="alert-item__title">{floorName}</div>
                    <div className="alert-item__desc">
                      ID: <b>{device.id}</b>
                      {device.smoke === 1 && <span className="tag">연기</span>}
                      {device.blackout === 1 && <span className="tag">정전</span>}
                      {device.ffFacility === 1 && <span className="tag">외부신호</span>}
                         {device.verkada === true && <span className="tag">버카다 환경센서</span>}
                      {device.motorFlag === 1 && device.smoke === 0 && device.blackout === 0 && device.ffFacility === 0 && device.verkada===false&&<span className="tag">원격 제어</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== 배터리 표시(그대로 사용) ===== */
function BatteryBadge({ adc }) {
  const pct = batteryPercentFromAdc(adc);
  const level = pct <= 15 ? 'low' : pct <= 40 ? 'mid' : 'ok';
  return (
    <div className={`marker-battery marker-battery--${level}`} title={`배터리 ${pct}% (ADC ${adc ?? 'N/A'})`}>
      <svg className="battery-svg" viewBox="0 0 28 14" width="24" height="12" aria-hidden>
        <rect x="1" y="1" width="23" height="12" rx="2" ry="2" className="b-outline" />
        <rect x="25" y="4" width="2" height="6" rx="1" className="b-tip" />
        <rect x="3" y="3" width={Math.max(0, Math.round((pct / 100) * 19))} height="8" className="b-fill" />
      </svg>
      <span className="battery-pct">{pct}%</span>
    </div>
  );
}
function batteryPercentFromAdc(adc) {
  const min = 1100, max = 1500;
  const v = Number(adc); if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(100, Math.round(((v - min) / (max - min)) * 100)));
}
